import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  Truck, 
  Package, 
  TrendingUp, 
  TrendingDown,
  CheckCircle,
  XCircle,
  Plus,
  Minus,
  RotateCcw,
  Eye,
  Filter,
  Users,
  MapPin,
  Star,
  Zap,
  RefreshCw,
  BarChart3,
  Settings
} from 'lucide-react';
import './InventoryDashboard.css';

const InventoryDashboard = () => {
  const [inventory, setInventory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustAnchor, setAdjustAnchor] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: 0,
    movement_type: 'in',
    notes: ''
  });
  const [filter, setFilter] = useState('all');
  
  // New state for enhanced inventory management
  const [reservations, setReservations] = useState([]);
  const [vendorPriority, setVendorPriority] = useState([]);
  const [stockAvailability, setStockAvailability] = useState({});
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [selectedZone, setSelectedZone] = useState('');
  const [zones] = useState(['North', 'South', 'East', 'West', 'Central']);

  // Fetch inventory data
  const fetchInventory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let vendorId = null;
      try {
        const v = JSON.parse(localStorage.getItem('vendorData') || '{}');
        vendorId = v?.id || v?.vendorId || null;
      } catch (_) {}
      const url = vendorId ? `/api/inventory/vendor/${vendorId}/summary` : `/api/inventory/vendor/summary`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInventory(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch active reservations
  const fetchReservations = async () => {
    try {
      const token = localStorage.getItem('token');
      let vendorId = null;
      try {
        const v = JSON.parse(localStorage.getItem('vendorData') || '{}');
        vendorId = v?.id || v?.vendorId || null;
      } catch (_) {}
      const query = vendorId ? `?vendorId=${vendorId}` : '';
      const response = await fetch(`/api/inventory/reservations${query}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReservations(data.reservations || []);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  // Fetch vendor priority queue for a product-zone combination
  const fetchVendorPriority = async (productId, zone) => {
    try {
      const response = await fetch(`/api/inventory/priority-queue/${productId}/${zone}`);
      if (response.ok) {
        const data = await response.json();
        setVendorPriority(data);
      }
    } catch (error) {
      console.error('Error fetching vendor priority:', error);
    }
  };

  // Fetch stock availability for a product in a zone
  const fetchStockAvailability = async (productId, zone) => {
    try {
      const response = await fetch(`/api/inventory/availability/${productId}/${zone}`);
      if (response.ok) {
        const data = await response.json();
        setStockAvailability(prev => ({
          ...prev,
          [`${productId}-${zone}`]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching stock availability:', error);
    }
  };

  // Reserve stock for an order
  const reserveStock = async (productId, quantity, zone, orderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/inventory/reserve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          productId,
          quantity,
          zone,
          orderId,
          customerLat: 0, // Will be updated with actual coordinates
          customerLng: 0
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Stock reserved successfully! Vendor ID: ${data.vendorId}, Reservation ID: ${data.reservationId}`);
        fetchReservations(); // Refresh reservations
        return data;
      } else {
        const error = await response.json();
        alert(`Failed to reserve stock: ${error.message}`);
      }
    } catch (error) {
      console.error('Error reserving stock:', error);
      alert('Error reserving stock');
    }
  };

  // Commit a reservation
  const commitReservation = async (reservationId, vendorId, productId, quantity) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/inventory/commit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reservationId,
          vendorId,
          productId,
          quantity
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('Reservation committed successfully!');
        fetchReservations(); // Refresh reservations
        fetchInventory(); // Refresh inventory
        return data;
      } else {
        const error = await response.json();
        alert(`Failed to commit reservation: ${error.message}`);
      }
    } catch (error) {
      console.error('Error committing reservation:', error);
      alert('Error committing reservation');
    }
  };

  // Release a reservation
  const releaseReservation = async (reservationId, vendorId, productId, quantity) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/inventory/release', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reservationId,
          vendorId,
          productId,
          quantity
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('Reservation released successfully!');
        fetchReservations(); // Refresh reservations
        fetchInventory(); // Refresh inventory
        return data;
      } else {
        const error = await response.json();
        alert(`Failed to release reservation: ${error.message}`);
      }
    } catch (error) {
      console.error('Error releasing reservation:', error);
      alert('Error releasing reservation');
    }
  };

  // Fetch alerts
  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchAlerts();
    fetchReservations();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stats = [
    {
      title: 'Low Stock Alerts',
      value: alerts.filter(alert => !alert.is_resolved).length.toString(),
      change: `${alerts.filter(alert => !alert.is_resolved && new Date(alert.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)).length} new today`,
      changeType: 'negative',
      icon: AlertTriangle,
      color: 'orange',
      description: 'Items below minimum threshold'
    },
    {
      title: 'Out of Stock',
      value: inventory.filter(item => item.stock_available <= 0).length.toString(),
      change: 'Critical items',
      changeType: 'negative',
      icon: XCircle,
      color: 'red',
      description: 'Items with zero stock'
    },
    {
      title: 'Total Products',
      value: inventory.length.toString(),
      change: 'Active inventory',
      changeType: 'positive',
      icon: Package,
      color: 'green',
      description: 'Total inventory items'
    },
    {
      title: 'Stock Value',
      value: `₹${inventory.reduce((sum, item) => sum + (item.stock_on_hand * item.price), 0).toFixed(2)}`,
      change: 'Current value',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'blue',
      description: 'Total inventory value'
    }
  ];

  // Filter inventory based on selected filter
  const filteredInventory = inventory.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'low-stock') return item.stock_available <= item.min_stock_level && item.stock_available > 0;
    if (filter === 'out-of-stock') return item.stock_available <= 0;
    if (filter === 'in-stock') return item.stock_available > item.min_stock_level;
    return true;
  });

  const lowStockItems = inventory.filter(item => 
    item.stock_available <= item.min_stock_level && item.stock_available > 0
  );

  const outOfStockItems = inventory.filter(item => item.stock_available <= 0);

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const productId = selectedProduct.id || selectedProduct.product_id;
      const response = await fetch(`/api/inventory/${productId}/adjust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(adjustmentData)
      });
      
      if (response.ok) {
        setShowAdjustModal(false);
        setSelectedProduct(null);
        setAdjustmentData({ quantity: 0, movement_type: 'in', notes: '' });
        fetchInventory();
        // trigger alerts recompute
        try {
          await fetch('/api/alerts/check', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
          fetchAlerts();
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error adjusting stock:', error);
    }
  };

  const openAdjustModal = (product, event) => {
    setSelectedProduct(product);
    setAdjustmentData({ quantity: 0, movement_type: 'in', notes: '' });
    if (event && event.currentTarget && typeof window !== 'undefined') {
      try {
        const rect = event.currentTarget.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const scrollX = window.scrollX || window.pageXOffset || 0;
        let top = rect.top + scrollY + rect.height + 8; // below the button
        let left = rect.left + scrollX;
        const maxWidth = Math.min(window.innerWidth * 0.92, 420);
        const overflow = (left + maxWidth) - (scrollX + window.innerWidth - 8);
        if (overflow > 0) {
          left = Math.max(8 + scrollX, left - overflow);
        }
        setAdjustAnchor({ top, left });
      } catch (_) {
        setAdjustAnchor(null);
      }
    } else {
      setAdjustAnchor(null);
    }
    setShowAdjustModal(true);
  };

  const getStatusBadge = (item) => {
    if (item.stock_available <= 0) return 'status-critical';
    if (item.stock_available <= item.min_stock_level) return 'status-low';
    return 'status-good';
  };

  const getStockStatus = (item) => {
    if (item.stock_available <= 0) return 'Out of Stock';
    if (item.stock_available <= item.min_stock_level) return 'Low Stock';
    return 'In Stock';
  };

  return (
    <div className="inventory-dashboard">
      <div className="dashboard-header">
        <h1>Inventory Dashboard</h1>
        <p>Monitor your inventory levels, track shipments, and manage stock alerts.</p>
      </div>

      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card stat-card-${stat.color}`}>
              <div className="stat-icon">
                <Icon size={24} />
              </div>
              <div className="stat-content">
                <h3 className="stat-title">{stat.title}</h3>
                <p className="stat-value">{stat.value}</p>
                <p className="stat-description">{stat.description}</p>
                <div className={`stat-change ${stat.changeType}`}>
                  {stat.changeType === 'positive' ? 
                    <TrendingUp size={16} /> : 
                    <TrendingDown size={16} />
                  }
                  <span>{stat.change}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-content">
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Inventory Overview</h2>
            <div className="section-actions">
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Items</option>
                <option value="low-stock">Low Stock</option>
                <option value="out-of-stock">Out of Stock</option>
                <option value="in-stock">In Stock</option>
              </select>
            </div>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>Stock On Hand</th>
                  <th>Reserved</th>
                  <th>Available</th>
                  <th>Min Level</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="loading-cell">Loading inventory...</td>
                  </tr>
                ) : filteredInventory.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-cell">No inventory items found</td>
                  </tr>
                ) : (
                  filteredInventory.map((item) => (
                    <tr key={item.id || item.product_id}>
                      <td data-label="Product Name">{item.product_name || item.name}</td>
                      <td className="sku" data-label="SKU">{item.sku}</td>
                      <td data-label="Stock On Hand">{item.stock_on_hand || 0}</td>
                      <td data-label="Reserved">{item.stock_reserved || 0}</td>
                      <td className={getStatusBadge(item)} data-label="Available">{item.stock_available || 0}</td>
                      <td data-label="Min Level">{item.min_stock_level || 0}</td>
                      <td data-label="Status">
                        <span className={`status-badge ${getStatusBadge(item)}`}>
                          {getStockStatus(item)}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <div className="action-buttons">
                          <button 
                            className="btn btn-sm btn-primary"
                          onClick={(e) => openAdjustModal(item, e)}
                          >
                            Adjust Stock
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h2>Low Stock Alerts</h2>
            <button className="btn btn-primary btn-sm" onClick={fetchAlerts}>Refresh</button>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>Current Stock</th>
                  <th>Min Required</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lowStockItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-cell">No low stock alerts</td>
                  </tr>
                ) : (
                  lowStockItems.map((item) => (
                    <tr key={item.id || item.product_id}>
                      <td data-label="Product Name">{item.product_name || item.name}</td>
                      <td className="sku" data-label="SKU">{item.sku}</td>
                      <td data-label="Current Stock">{item.stock_available || 0}</td>
                      <td data-label="Min Required">{item.min_stock_level || 0}</td>
                      <td data-label="Status">
                        <span className={`status-badge ${getStatusBadge(item)}`}>
                          {getStockStatus(item)}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <div className="action-buttons">
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={(e) => openAdjustModal(item, e)}
                          >
                            Restock
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h2>Stock Alerts</h2>
            <button className="btn btn-primary btn-sm" onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                await fetch('/api/alerts/check', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                fetchAlerts();
              } catch (e) {}
            }}>Check Alerts</button>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>Alert Type</th>
                  <th>Current Stock</th>
                  <th>Threshold</th>
                  <th>Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alerts.filter(alert => !alert.is_resolved).length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-cell">No active alerts</td>
                  </tr>
                ) : (
                  alerts.filter(alert => !alert.is_resolved).map((alert) => (
                    <tr key={alert.id}>
                      <td data-label="Product Name">{alert.product_name}</td>
                      <td className="sku" data-label="SKU">{alert.sku}</td>
                      <td data-label="Alert Type">
                        <span className={`status-badge ${alert.alert_type === 'out_of_stock' ? 'status-critical' : 'status-low'}`}>
                          {alert.alert_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td data-label="Current Stock">{alert.current_stock}</td>
                      <td data-label="Threshold">{alert.threshold_value}</td>
                      <td data-label="Message">{alert.message}</td>
                      <td data-label="Actions">
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-secondary" onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(`/api/alerts/${alert.id}/resolve`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
                              if (res.ok) fetchAlerts();
                            } catch (e) {}
                          }}>Resolve</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      {showAdjustModal && selectedProduct && (
        <div className={`modal-overlay ${isMobile ? 'mobile-popover' : ''}`} onClick={() => setShowAdjustModal(false)}>
          <div className="modal" style={isMobile && adjustAnchor ? { position: 'absolute', top: adjustAnchor.top, left: adjustAnchor.left, width: 'min(92vw, 420px)' } : {}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Adjust Stock - {selectedProduct.product_name || selectedProduct.name}</h2>
              <button className="close-btn" onClick={() => setShowAdjustModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleAdjustStock} className="adjust-form">
              <div className="form-group">
                <label>Movement Type</label>
                <select
                  value={adjustmentData.movement_type}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, movement_type: e.target.value }))}
                >
                  <option value="in">Stock In (+)</option>
                  <option value="out">Stock Out (-)</option>
                  <option value="adjustment">Adjustment (=)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  value={adjustmentData.quantity}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                  min="0"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={adjustmentData.notes}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  placeholder="Optional notes about this stock adjustment"
                />
              </div>

              <div className="current-stock">
                <h4>Current Stock Levels</h4>
                <div className="stock-info">
                  <div className="stock-item">
                    <span className="label">On Hand:</span>
                    <span className="value">{selectedProduct.stock_on_hand || 0}</span>
                  </div>
                  <div className="stock-item">
                    <span className="label">Reserved:</span>
                    <span className="value">{selectedProduct.stock_reserved || 0}</span>
                  </div>
                  <div className="stock-item">
                    <span className="label">Available:</span>
                    <span className="value">{selectedProduct.stock_available || 0}</span>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdjustModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Adjust Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Inventory Management Sections */}
      
      {/* Active Reservations Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Active Reservations</h2>
          <div className="section-actions">
            <button className="btn btn-primary btn-sm" onClick={fetchReservations}>
              <RefreshCw size={16} />
              Refresh
            </button>
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={() => setShowReservationModal(true)}
            >
              <Plus size={16} />
              New Reservation
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Reservation ID</th>
                <th>Product</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Type</th>
                <th>Status</th>
                <th>Expires At</th>
                <th>Order Number</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reservations.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty-cell">No active reservations</td>
                </tr>
              ) : (
                reservations.map((reservation) => (
                  <tr key={reservation.reservation_id}>
                    <td data-label="Reservation ID">#{reservation.reservation_id}</td>
                    <td data-label="Product">{reservation.product_name}</td>
                    <td className="sku" data-label="SKU">{reservation.sku}</td>
                    <td data-label="Quantity">{reservation.quantity}</td>
                    <td data-label="Type">
                      <span className="status-badge status-info">
                        {reservation.reservation_type}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className="status-badge status-warning">
                        {reservation.status}
                      </span>
                    </td>
                    <td data-label="Expires At">{new Date(reservation.expires_at).toLocaleString()}</td>
                    <td data-label="Order Number">{reservation.order_number || 'N/A'}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => commitReservation(
                            reservation.reservation_id,
                            (JSON.parse(localStorage.getItem('vendorData') || '{}')?.id || 0),
                            reservation.product_id,
                            reservation.quantity
                          )}
                        >
                          Commit
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => releaseReservation(
                            reservation.reservation_id,
                            (JSON.parse(localStorage.getItem('vendorData') || '{}')?.id || 0),
                            reservation.product_id,
                            reservation.quantity
                          )}
                        >
                          Release
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vendor Priority Queue Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Vendor Priority Queue</h2>
          <div className="section-actions">
            <select 
              value={selectedZone} 
              onChange={(e) => setSelectedZone(e.target.value)}
              className="form-select"
            >
              <option value="">Select Zone</option>
              {zones.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
            <button 
              className="btn btn-primary btn-sm" 
              onClick={() => setShowPriorityModal(true)}
            >
              <BarChart3 size={16} />
              View Priority
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Product</th>
                <th>Zone</th>
                <th>Priority Score</th>
                <th>Distance (km)</th>
                <th>SLA (min)</th>
                <th>Rating</th>
                <th>Stock Available</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {vendorPriority.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty-cell">No vendor priority data available</td>
                </tr>
              ) : (
                vendorPriority.map((vendor, index) => (
                  <tr key={`${vendor.vendor_id}-${vendor.product_id}`}>
                    <td data-label="Vendor">
                      <div className="vendor-info">
                        <strong>{vendor.shop_name}</strong>
                        <span className="vendor-id">#{vendor.vendor_id}</span>
                      </div>
                    </td>
                    <td data-label="Product">Product #{vendor.product_id}</td>
                    <td data-label="Zone">{selectedZone}</td>
                    <td data-label="Priority Score">
                      <span className="priority-score">
                        {vendor.priority_score.toFixed(2)}
                      </span>
                    </td>
                    <td data-label="Distance (km)">{vendor.distance_km?.toFixed(2) || 'N/A'}</td>
                    <td data-label="SLA (min)">{vendor.sla_minutes}</td>
                    <td data-label="Rating">
                      <div className="rating">
                        <Star size={14} fill="gold" />
                        {vendor.rating}
                      </div>
                    </td>
                    <td className={vendor.stock_available > 0 ? 'status-good' : 'status-critical'} data-label="Stock Available">
                      {vendor.stock_available}
                    </td>
                    <td data-label="Price">₹{vendor.price}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock Availability Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Stock Availability by Zone</h2>
          <div className="section-actions">
            <select 
              value={selectedZone} 
              onChange={(e) => setSelectedZone(e.target.value)}
              className="form-select"
            >
              <option value="">Select Zone</option>
              {zones.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
            <button 
              className="btn btn-primary btn-sm" 
              onClick={() => {
                if (selectedZone && inventory.length > 0) {
                  inventory.forEach(item => {
                    fetchStockAvailability(item.product_id, selectedZone);
                  });
                }
              }}
            >
              <RefreshCw size={16} />
              Check Availability
            </button>
          </div>
        </div>
        <div className="availability-grid">
          {Object.entries(stockAvailability).map(([key, data]) => (
            <div key={key} className="availability-card">
              <div className="card-header">
                <h4>Product #{data.productId}</h4>
                <span className="zone-badge">{data.zone}</span>
              </div>
              <div className="card-content">
                <div className="availability-stats">
                  <div className="stat">
                    <span className="label">Total Available:</span>
                    <span className="value">{data.totalAvailable}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Vendor Count:</span>
                    <span className="value">{data.vendorCount}</span>
                  </div>
                </div>
                <div className="vendor-list">
                  {data.vendors.map((vendor, index) => (
                    <div key={index} className="vendor-item">
                      <span className="vendor-name">{vendor.shop_name}</span>
                      <span className="vendor-stock">{vendor.stock_available} units</span>
                      <span className="vendor-price">₹{vendor.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reservation Modal */}
      {showReservationModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Reservation</h3>
              <button 
                className="btn-close" 
                onClick={() => setShowReservationModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const productId = formData.get('productId');
              const quantity = formData.get('quantity');
              const zone = formData.get('zone');
              const orderId = formData.get('orderId');
              
              await reserveStock(parseInt(productId), parseInt(quantity), zone, parseInt(orderId));
              setShowReservationModal(false);
            }}>
              <div className="form-group">
                <label>Product ID</label>
                <input type="number" name="productId" required />
              </div>
              <div className="form-group">
                <label>Quantity</label>
                <input type="number" name="quantity" required min="1" />
              </div>
              <div className="form-group">
                <label>Zone</label>
                <select name="zone" required>
                  {zones.map(zone => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Order ID</label>
                <input type="number" name="orderId" required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowReservationModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Reservation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Priority Queue Modal */}
      {showPriorityModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Vendor Priority Queue</h3>
              <button 
                className="btn-close" 
                onClick={() => setShowPriorityModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Product ID</label>
                <input 
                  type="number" 
                  id="priorityProductId" 
                  placeholder="Enter product ID"
                />
              </div>
              <div className="form-group">
                <label>Zone</label>
                <select id="priorityZone">
                  {zones.map(zone => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  const productId = document.getElementById('priorityProductId').value;
                  const zone = document.getElementById('priorityZone').value;
                  if (productId && zone) {
                    fetchVendorPriority(parseInt(productId), zone);
                  }
                }}
              >
                Load Priority Queue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDashboard;
